/** Notifications (M2.1): the in-app list (newest first, cursor-paged), unread first-class, "Mark all read". Tapping one opens its link. */
import { useMemo, useState } from "react";
import { navigate } from "../../app/router";
import type { Notification } from "../../api/types";
import { Icon, type IconName } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Button, Chip, EmptyState, ErrorNote, PageHeader, QueryView } from "../../components/ui";
import { useApi, useMutation, useQuery } from "../../data/hooks";
import { dateTime, relative } from "../../lib/format";

const PAGE = 30;
const SEVERITY: Record<Notification["severity"], { tone: string; icon: IconName }> = {
  critical: { tone: "danger", icon: "alert" }, attention: { tone: "attention", icon: "bell" }, info: { tone: "info", icon: "check" },
};

export function NotificationsScreen() {
  const api = useApi();
  const toast = useToast();
  const [unread, setUnread] = useState(false);
  const key = `notifications/list/${unread ? "unread" : "all"}`;
  const first = useQuery(key, () => api.listNotifications({ unread, limit: PAGE }), { staleMs: 10_000 });
  const [more, setMore] = useState<{ key: string; items: Notification[]; cursor: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const extra = more?.key === key ? more : null;
  const cursor = extra ? extra.cursor : first.data?.next_cursor ?? null;
  const items = useMemo(() => {
    const seen = new Set<string>();
    return [...(first.data?.notifications ?? []), ...(extra?.items ?? [])].filter((n) => (seen.has(n.id) ? false : (seen.add(n.id), true)));
  }, [first.data, extra]);
  const unreadCount = first.data?.unread_count ?? 0;
  const readAll = useMutation(() => api.markAllNotificationsRead(), { invalidate: ["notifications"], onSuccess: () => toast.ok("All caught up."), onError: (e) => toast.error(e) });
  const read = useMutation((id: string) => api.markNotificationRead(id), { invalidate: ["notifications"] });
  const open = (n: Notification) => {
    if (!n.read_at) void read.mutate(n.id);
    if (n.link && n.link.startsWith("/")) navigate(n.link);
  };
  const loadMore = async () => {
    if (!cursor) return;
    setLoading(true);
    setMoreError(null);
    try {
      const page = await api.listNotifications({ unread, cursor, limit: PAGE });
      setMore({ key, items: [...(extra?.items ?? []), ...page.notifications], cursor: page.next_cursor });
    } catch (e) { setMoreError(e); } finally { setLoading(false); }
  };
  return (
    <div className="stack-lg">
      <PageHeader title="Notifications" subtitle={unreadCount ? `${unreadCount} unread` : "You're all caught up."}
        actions={<Button variant="secondary" icon="check" disabled={!unreadCount} busy={readAll.pending} onClick={() => void readAll.mutate()}>Mark all read</Button>} />
      <div className="chips" role="group" aria-label="Show">
        <Chip pressed={!unread} onClick={() => setUnread(false)}>All</Chip>
        <Chip pressed={unread} onClick={() => setUnread(true)} count={unreadCount || undefined}>Unread</Chip>
      </div>
      <QueryView q={first}>{() => items.length ? (
        <>
          <ul className="list notif-list" aria-label="Notifications">
            {items.map((n) => {
              const sv = SEVERITY[n.severity];
              return (
                <li key={n.id} className={n.read_at ? "" : "is-unread"}>
                  <button type="button" className="list-row notif-row" onClick={() => open(n)} aria-label={`${n.read_at ? "" : "Unread: "}${n.title}`}>
                    <span className={`list-icon tone-${sv.tone}`}><Icon name={sv.icon} size={20} /></span>
                    <span className="list-main">
                      <span className="list-title">{n.title}</span>
                      {n.body && <span className="list-sub">{n.body}</span>}
                      <span className="list-sub"><time dateTime={n.created_at} title={dateTime(n.created_at)}>{relative(n.created_at)}</time></span>
                    </span>
                    {!n.read_at && <span className="unread-dot" aria-hidden />}
                  </button>
                </li>
              );
            })}
          </ul>
          {moreError ? <ErrorNote error={moreError} onRetry={() => void loadMore()} /> : null}
          {cursor && <Button variant="secondary" block busy={loading} onClick={() => void loadMore()}>Load more</Button>}
        </>
      ) : <EmptyState icon="bell" title={unread ? "No unread notifications" : "No notifications yet"}>Failures, reviews ready, plans and posts that went live show up here — and on this device, if you turn push on in Settings.</EmptyState>}</QueryView>
    </div>
  );
}
